import { useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AppLoader from "components/AppLoader";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import appSettings from "appsettings.json";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function Cover() {
  const [phoneNo, setPhoneNo] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const requestOtp = async () => {
    if (!phoneNo.trim()) {
      setErrorMessage("Phone number is required.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const requestOtpUrl = `${appSettings.baseUrl}${
      appSettings.endpoints.requestForgotPasswordOtp
    }?phoneNo=${encodeURIComponent(phoneNo)}`;

    try {
      const response = await fetch(requestOtpUrl, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to send OTP.");
      }

      setOtpStep(true);
      setSuccessMessage("OTP sent. Please check your registered phone number.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!phoneNo.trim() || !otp.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setErrorMessage("Phone number, OTP and password fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Password confirmation does not match.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const resetPasswordUrl = `${appSettings.baseUrl}${
      appSettings.endpoints.resetForgotPasswordWithOtp
    }?phoneNo=${encodeURIComponent(phoneNo)}&otp=${encodeURIComponent(
      otp
    )}&newPassword=${encodeURIComponent(newPassword)}`;

    try {
      const response = await fetch(resetPasswordUrl, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Password reset failed.");
      }

      setSuccessMessage("Password reset successfully. You can now sign in.");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setOtpStep(false);
    } catch (error) {
      setErrorMessage(error.message || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          py={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Forgot Password
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Reset your password with OTP verification
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Phone Number"
                value={phoneNo}
                onChange={(e) => setPhoneNo(e.target.value)}
                fullWidth
              />
            </MDBox>

            {otpStep && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    fullWidth
                  />
                </MDBox>
                <MDBox mt={-1} mb={2}>
                  <MDTypography variant="caption" color="warning" fontWeight="regular">
                    OTP is valid for 1 minute.
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                  />
                </MDBox>
              </>
            )}

            {errorMessage && (
              <MDBox mb={2}>
                <MDTypography variant="button" color="error" fontWeight="regular">
                  {errorMessage}
                </MDTypography>
              </MDBox>
            )}

            {successMessage && (
              <MDBox mb={2}>
                <MDTypography variant="button" color="success" fontWeight="regular">
                  {successMessage}
                </MDTypography>
              </MDBox>
            )}

            <MDBox mt={3} mb={1}>
              <MDButton
                variant="gradient"
                color="success"
                fullWidth
                onClick={otpStep ? resetPassword : requestOtp}
                disabled={loading}
              >
                {loading ? (
                  <AppLoader size={20} color="currentColor" />
                ) : otpStep ? (
                  "Reset Password"
                ) : (
                  "Send OTP"
                )}
              </MDButton>
            </MDBox>

            <MDBox mt={2} textAlign="center">
              <MDTypography
                component={Link}
                to="/authentication/sign-in"
                variant="button"
                color="success"
                fontWeight="medium"
                textGradient
              >
                Back to Sign In
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Cover;
